import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GoogleLoginButton from '../components/GoogleLoginButton';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, isAuthenticated } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // 이미 로그인된 경우 홈으로
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }

    setIsLoading(true);
    try {
      await login({ email, password });
      navigate('/');
    } catch (error) {
      // 에러는 AuthContext에서 toast로 표시
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-pink-700 flex flex-col">
      {/* 로그인 폼 영역 */}
      <div className="flex-grow flex items-center justify-center px-4 py-4">
        <div className="max-w-md w-full">
          {/* 로고 - 축소 */}
          <div className="text-center mb-4">
            <h1 className="text-4xl font-black mb-1">🧡</h1>
            <h2 className="text-2xl font-bold text-white mb-1">AZOCSII</h2>
            <p className="text-sm text-white/80">돈까스 아조씨</p>
          </div>

          {/* 로그인 폼 - 압축 */}
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-white/20">
            <h3 className="text-xl font-bold text-white mb-4 text-center">로그인</h3>

            {/* Google 로그인 — 상단 배치 */}
            <GoogleLoginButton
              className="w-full"
              label="Google 계정으로 시작하기"
              onSuccess={async (firebaseUser) => {
                try {
                  const result = await loginWithGoogle(firebaseUser);
                  if (result.isNewUser) {
                    navigate('/register', {
                      state: {
                        email: result.email,
                        name: result.name,
                        picture: result.picture,
                        fromGoogle: true,
                      },
                    });
                  } else {
                    navigate('/');
                  }
                } catch {
                  // 에러는 AuthContext에서 toast로 이미 처리됨
                }
              }}
            />

            {/* 구분선 */}
            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-white/20" />
              <span className="text-xs text-white/50">또는 이메일로 로그인</span>
              <div className="h-px flex-1 bg-white/20" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 이메일 */}
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-white mb-1">
                  이메일
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm text-sm"
                  placeholder="your@email.com"
                  disabled={isLoading}
                />
              </div>

              {/* 비밀번호 */}
              <div>
                <label htmlFor="password" className="block text-xs font-medium text-white mb-1">
                  비밀번호
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent backdrop-blur-sm text-sm"
                  placeholder="••••••••"
                  disabled={isLoading}
                />
              </div>

              {/* 로그인 버튼 */}
              <button
                type="submit"
                disabled={isLoading || !email || !password}
                className="w-full bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold py-2.5 px-6 rounded-xl hover:from-orange-600 hover:to-pink-600 transform hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? '로그인 중...' : '이메일로 로그인'}
              </button>
            </form>

            {/* 회원가입 링크 */}
            <div className="mt-4 text-center">
              <p className="text-white/80 text-xs">
                계정이 없으신가요?{' '}
                <Link
                  to="/register"
                  className="text-orange-300 hover:text-orange-200 font-semibold underline"
                >
                  회원가입
                </Link>
              </p>
            </div>
          </div>

          {/* 홈으로 */}
          <div className="mt-3 text-center">
            <Link
              to="/"
              className="text-white/60 hover:text-white/90 text-xs transition-colors"
            >
              ← 홈으로 돌아가기
            </Link>
          </div>
        </div>
      </div>

      {/* Footer - 페이지 하단 */}
      <footer className="bg-primary text-white py-4">
        <div className="container mx-auto px-4 text-center">
          <p className="text-base mb-1">
            🧡{' '}
            <span className="text-red-500">A</span>
            <span className="text-orange-500">Z</span>
            <span className="text-yellow-400">O</span>
            <span className="text-green-500">C</span>
            <span className="text-blue-500">S</span>
            <span className="text-indigo-500">I</span>
            <span className="text-purple-500">I</span>
            {' '}- 돈까스 아조씨
          </p>
          <p className="text-xs text-gray-300">
            © 2026 AZOCSII Project.
          </p>
          <p className="text-xs text-secondary mt-0.5">
            <span className="font-bold text-accent">A</span>uthentic{' '}
            <span className="font-bold text-accent">Z</span>eal{' '}
            <span className="font-bold text-accent">O</span>asis —{' '}
            <span className="font-bold text-accent">C</span>ounselor{' '}
            <span className="font-bold text-accent">S</span>olidarity{' '}
            <span className="font-bold text-accent">I</span>nitiative
          </p>
          <p className="text-xs text-gray-400 mt-1">
            소명: 일상 회복 | A to Z, For Every Humanist in the World
          </p>
        </div>
      </footer>
    </main>
  );
};

export default LoginPage;
